import { useModal } from '@ebay/nice-modal-react';
import { meetingTestingConstants } from '@shared/constants';
import cx from 'classnames';
import { t } from 'i18next';
import { EventType } from 'matrix-js-sdk/src';
import { Trans } from 'react-i18next';

import { ReactComponent as Bin } from 'assets/icons/Bin.svg';
import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import ConfirmationModal from 'components/ConfirmationModal';
import { boldElement } from 'components/ConfirmPopup';
import OptionsDropdown from 'components/OptionsDropdown/OptionsDropdown';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import { isEmailFormat } from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import {
  assignmentMatchesMember,
  findThirdPartyInviteEventIdForEmail,
} from 'modules/Workspaces/WorkspaceSettingsModal/MembersTab/utils';
import { getWorkspaceName } from 'modules/Workspaces/WorkspaceSettingsModal/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, WORKSPACE_KICK_REASON } from 'utils/matrixUtils';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

import { Member } from '../utils';

interface MemberOptionsDropdownProps {
  member: Member;
}

const iconClassNames = 'mr-2 h-5 w-5 shrink-0 stroke-1.5';

const MemberOptionsDropdown = ({ member }: MemberOptionsDropdownProps) => {
  const confirmationModal = useModal(ConfirmationModal);
  const { workspaceId, workspaceRoles, roleAssignments } = useWorkspacesContext({
    requireWorkspaceId: true,
  });
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();

  const resetRole = async () => {
    const workspaceRoom = matrixClient.getRoom(workspaceId);
    if (!workspaceRoom) throw new Error('Workspace room not found');

    const updatedRoleAssignments = roleAssignments.filter((assignment) => !assignmentMatchesMember(assignment, member));

    await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
      version: WORKSPACE_ROLES_EVENT_VERSION,
      roles: workspaceRoles,
      assignments: updatedRoleAssignments,
    });
  };

  const redactThirdPartyInvite = async () => {
    const room = matrixClient.getRoom(workspaceId);
    if (!room) return;

    let eventToRedact: string | undefined;

    if (member.isPendingInvite && isEmailFormat(member.id)) {
      const thirdPartyEvents = room.currentState.getStateEvents(EventType.RoomThirdPartyInvite);
      eventToRedact = findThirdPartyInviteEventIdForEmail(thirdPartyEvents, member.id);
    } else {
      const memberEvent = room.currentState.getStateEvents(EventType.RoomMember, member.id);
      const inviteToken = memberEvent?.getContent()?.third_party_signed?.token as string | undefined;

      if (inviteToken) {
        const thirdPartyEvent = room.currentState.getStateEvents(EventType.RoomThirdPartyInvite, inviteToken);
        eventToRedact = thirdPartyEvent?.getId();
      }
    }

    if (eventToRedact) await matrixClient.redactEvent(workspaceId, eventToRedact);
  };

  const handleRemoveMember = async () => {
    const { id, powerLevel, isPendingInvite } = member;
    const currentUserId = matrixClient.getUserId();
    const isCurrentUser = currentUserId === id;
    const currentUserPowerLevel =
      matrixClient.getRoom(workspaceId ?? '')?.getMember(currentUserId ?? '')?.powerLevel ?? 0;
    const isTargetMemberLowerPowerLevel = currentUserPowerLevel > powerLevel;

    const { kickMember, adjustRoles } = calculatePermissions(matrixClient, workspaceId);

    if (!isTargetMemberLowerPowerLevel || !kickMember || isCurrentUser) {
      return pushNoPermissionToast();
    }

    try {
      if (adjustRoles) {
        await resetRole();
      }
      await redactThirdPartyInvite();
      if (!isPendingInvite || !isEmailFormat(id)) {
        await matrixClient.kick(workspaceId, id, WORKSPACE_KICK_REASON);
      }

      pushToast({
        variant: 'success',
        title: isPendingInvite ? t('workspaces.cancel_invite_success') : t('workspaces.kick_success'),
      });
    } catch (error) {
      logger.error(`Failed to remove member ${id} from workspace ${workspaceId}`, { error });
      pushToast({
        variant: 'warning',
        title: isPendingInvite ? t('workspaces.cancel_invite_failed') : t('workspaces.kick_failed'),
      });
    }
  };

  const { id: memberId, name, email, phone, isPendingInvite } = member;
  const displayName = name || email || phone || memberId;
  const workspaceName = getWorkspaceName(matrixClient, workspaceId);

  const confirmationMessage = isPendingInvite ? (
    <Trans
      i18nKey="workspaces.cancel_invite_confirmation_question"
      values={{ userName: displayName, workspaceName }}
      components={{ bold: boldElement }}
    />
  ) : (
    <Trans
      i18nKey="workspaces.remove_member_confirmation_question"
      values={{ userName: displayName, workspaceName }}
      components={{ bold: boldElement }}
    />
  );

  const openRemoveConfirmationModal = () =>
    confirmationModal.show({
      icon: Delete,
      message: confirmationMessage,
      confirmButtonText: isPendingInvite ? t('workspaces.cancel_invite') : t('remove'),
      onConfirm: handleRemoveMember,
    });

  const options = [
    {
      title: isPendingInvite ? t('workspaces.cancel_invite') : t('remove'),
      icon: <Bin className={cx('stroke-alert-50', iconClassNames)} />,
      className: 'text-alert-50',
      onClick: openRemoveConfirmationModal,
      dataTestId: meetingTestingConstants.removeUser,
    },
  ];

  return <OptionsDropdown options={options} openInPortal />;
};

export default MemberOptionsDropdown;
